const JBROWSE2_URI = process.env.VUE_APP_JBROWSE2_DOMAIN;
const DISPLAY_MARGIN_PERCENTAGE = 5;
const CHROMOSOME_MITOCHONDRIAL_GENOME = "ChrM";

function getDefaultJBrowse2Url() {
  return `${JBROWSE2_URI}/index.html`;
}

function getMarker(chr, start, end) {
  const marginLength = Math.round((Number(end) - Number(start)) * DISPLAY_MARGIN_PERCENTAGE / 100);
  const bufferedStart = Number(start) - marginLength;
  const bufferedEnd = Number(end) + marginLength;
  return `${chr}:${bufferedStart}..${bufferedEnd}`;
}

const JBROWSE2_ASSEMBLY = "tair10";

// TAIR3-920: the Locus and Gene pages view the TAIR12 genome (JBrowse2
// assembly "Col-CC") rather than tair10. Map-element views (markers,
// polymorphisms, transposons, tiling path) stay on tair10 — their tracks are
// TAIR9/Araport11 data that is not lifted to Col-CC.
const JBROWSE2_TAIR12_ASSEMBLY = "Col-CC";
const TAIR12_REFSEQ_TRACK = "Col-CC-RefSeq";
const TAIR12_PROTEIN_CODING_TRACK = "Protein_Coding_Genes";
const TAIR12_SMALL_NCRNA_TRACK = "Small_Non_Coding_RNAs";
const TAIR12_LONG_NCRNA_TRACK = "Long_Non_Coding_RNAs";
const TAIR12_PSEUDOGENE_TRACK = "Pseudogenes";
const TAIR12_ALL_GENE_TRACKS = [
    TAIR12_PROTEIN_CODING_TRACK,
    TAIR12_SMALL_NCRNA_TRACK,
    TAIR12_LONG_NCRNA_TRACK,
    TAIR12_PSEUDOGENE_TRACK,
];
// "TAIR12 - All Features": every TAIR12 feature on one track (TAIR3-1087).
const TAIR12_ALL_FEATURES_TRACK = "TAIR12_Full";

// TAIR3-893: normalize a chromosome to a JBrowse2 `tair10` refName. The value
// arrives in several encodings (bare 1..5, CP/MT, C/M, already Chr-prefixed,
// the mis-generated ChrCP/ChrMT) and sometimes as a number. Parse to a
// canonical code, then format to the refName. Defensive client patch over
// inconsistent upstream data; the source-of-truth fix is TAIR3-895.

// Accepted spellings (Chr-stripped, upper-cased) -> canonical chromosome code.
const CHROMOSOME_CODES = {
    "1": "1", "2": "2", "3": "3", "4": "4", "5": "5",
    "C": "C", "CP": "C", "CHLOROPLAST": "C", "CHLOROPLAST GENOME": "C",
    "M": "M", "MT": "M", "MITOCHONDRIA": "M", "MITOCHONDRIAL GENOME": "M",
};

// Canonical code -> tair10 assembly refName (Chr1..Chr5, ChrC, ChrM; no aliases).
const JBROWSE2_REFNAMES = {
    "1": "Chr1", "2": "Chr2", "3": "Chr3", "4": "Chr4", "5": "Chr5",
    "C": "ChrC", "M": CHROMOSOME_MITOCHONDRIAL_GENOME,
};

function parseChromosome(chr) {
    let code = null;
    if (chr != null) {
        const key = String(chr).trim().replace(/^chr/i, "").toUpperCase();
        code = CHROMOSOME_CODES[key] || null;
    }
    return code;
}

// An absent chromosome (null/undefined/blank, e.g. the " " Solr returns for an
// unmapped marker) collapses to null; a present-but-unrecognized value passes
// through unchanged so we never fabricate a refName.
function toJBrowse2RefName(chr) {
    const code = parseChromosome(chr);
    let refName;
    if (code) {
        refName = JBROWSE2_REFNAMES[code];
    } else if (chr == null || String(chr).trim() === "") {
        refName = null;
    } else {
        refName = String(chr).trim();
    }
    return refName;
}

// A nuclear chromosome refName (Chr1..Chr5). Some tracks have per-chromosome
// data for these only — see NUCLEAR_ONLY_TRACKS. (TAIR3-893)
function isNuclearRefName(refName) {
    return /^Chr[1-5]$/.test(refName);
}

// NCList tracks whose per-chromosome data exists for the nuclear chromosomes
// only; on the organelles (ChrC/ChrM) they 403. buildJBrowse2Url drops them from
// any non-nuclear view. TAIR9_tDNAs is intentionally NOT listed — it has
// organelle data. (TAIR3-893)
const NUCLEAR_ONLY_TRACKS = new Set(["TDNA-Seq", "TAIR9_polymorphisms"]);

// Shared query build for the JBrowse2 views. Normalizes the chromosome once and
// uses it for both the loc and to drop tracks that have no data on that refseq.
// `tracks` is the view's full track list (an array).
function buildJBrowse2Url(chr, start, end, tracks, tracklist, assembly = JBROWSE2_ASSEMBLY) {
    const refName = toJBrowse2RefName(chr);
    // Drop tracks that have no data on non-nuclear refseqs (they 403).
    if (!isNuclearRefName(refName)) {
        tracks = tracks.filter((track) => !NUCLEAR_ONLY_TRACKS.has(track));
    }
    const params = new URLSearchParams();
    params.set('assembly', assembly);
    params.set('loc', getMarker(refName, start, end));
    params.set('tracks', tracks.join(","));
    if (tracklist) {
        params.set('tracklist', 'true');
    }
    return `${JBROWSE2_URI}/?${params.toString()}`;
}

// Locus / Gene page views (TAIR3-920): TAIR12 Col-CC assembly, reference
// sequence plus the gene track for the model type. The embedded (slim) view
// hides the track list; the full-screen link shows it.
function getSlimViewUrl2(chr, start, end, geneModelType) {
    const tracklist = false;
    return buildJBrowse2Url(chr, start, end, getTair12ViewJBrowse2Tracks(geneModelType), tracklist, JBROWSE2_TAIR12_ASSEMBLY);
}

function getFullViewUrl2(chr, start, end, geneModelType) {
    const tracklist = true;
    return buildJBrowse2Url(chr, start, end, getTair12ViewJBrowse2Tracks(geneModelType), tracklist, JBROWSE2_TAIR12_ASSEMBLY);
}

// Which TAIR12 gene track to show for a gene model type (T.B., TAIR3-920):
//   protein_coding                                  -> Protein_Coding_Genes
//   long_noncoding_rna / antisense_long_noncoding_rna -> Long_Non_Coding_RNAs
//   any other *rna* type                            -> Small_Non_Coding_RNAs
//   pseudogene                                      -> Pseudogenes
// Types with no dedicated track (uORF, novel_transcribed_region,
// transposable_element_gene, unknown) show all four.
// No type at all (the 119 TAIR12 loci with no gene model, TAIR3-1087) shows
// the single "TAIR12 - All Features" track: with four stacked tracks the
// locus sits below the visible part of the embedded frame.
function getTair12GeneTracks(geneModelType) {
    let tracks;
    const type = geneModelType == null ? "" : String(geneModelType).trim().toLowerCase();
    if (type === "") {
        tracks = [TAIR12_ALL_FEATURES_TRACK];
    } else if (type === "protein_coding") {
        tracks = [TAIR12_PROTEIN_CODING_TRACK];
    } else if (type === "long_noncoding_rna" || type === "antisense_long_noncoding_rna") {
        tracks = [TAIR12_LONG_NCRNA_TRACK];
    } else if (type.includes("rna")) {
        tracks = [TAIR12_SMALL_NCRNA_TRACK];
    } else if (type === "pseudogene") {
        tracks = [TAIR12_PSEUDOGENE_TRACK];
    } else {
        tracks = TAIR12_ALL_GENE_TRACKS.slice();
    }
    return tracks;
}

function getTair12ViewJBrowse2Tracks(geneModelType) {
    return [TAIR12_REFSEQ_TRACK].concat(getTair12GeneTracks(geneModelType));
}

function getMapElementViewUrl2(chr, start, end, mapElementType) {
    const tracklist = true;
    return buildJBrowse2Url(chr, start, end, getMapElementViewJBrowse2Tracks(mapElementType), tracklist);
}

// TAIR3-1085: transposon and marker coordinates now come from the Col-CC.v2
// map, so view them on the Col-CC assembly (tair10 would land elsewhere):
// reference sequence and gene tracks, plus the TE tracks for a transposon.
// Col-CC has no marker track; a marker view shows the genes around it.
const TAIR12_TE_TRACKS = [
    "Helitrons",
    "LTR_Retrotransposons",
    "Non_LTR_Retrotransposons",
    "TIR_DNA_Transposons",
];

function getTair12MapElementViewUrl(chr, start, end, mapElementType) {
    const tracklist = true;
    return buildJBrowse2Url(chr, start, end, getTair12MapElementViewJBrowse2Tracks(mapElementType), tracklist, JBROWSE2_TAIR12_ASSEMBLY);
}

function getTair12MapElementViewJBrowse2Tracks(mapElementType) {
    let tracks = [TAIR12_REFSEQ_TRACK].concat(TAIR12_ALL_GENE_TRACKS);
    if (mapElementType != null && mapElementType.toLowerCase() === "transposon") {
        tracks = tracks.concat(TAIR12_TE_TRACKS);
    }
    return tracks;
}

// Returns the view's full track list (an array). Per-refseq availability
// (dropping nuclear-only tracks on organelles) is applied by buildJBrowse2Url.
function getMapElementViewJBrowse2Tracks(mapElementType) {
    let tracks;
    if (mapElementType != null) {
        tracks = ["Araport11_Loci"]; // Araport11 - Gene Locus
        switch (mapElementType.toLowerCase()) {
            case "genetic_marker":
            case "marker":
                tracks.push("TAIR9_markers"); // Genetic Marker (TAIR9)
                break;
            case "polymorphism":
            case "polyallele":
                // Polymorphism (TAIR9), TDNA Classical (TAIR9), TDNA-seq
                tracks.push("TAIR9_polymorphisms", "TAIR9_tDNAs", "TDNA-Seq");
                break;
            case "transposon":
                tracks.push("Araport11_TEs"); // Araport11 - Transposable elements
                break;
            case "assembly_unit":
                tracks.push("Tilingpath"); // Tiling Path (TAIR9)
                break;
            default:
                tracks = ["Araport11_Loci", "Araport11_GeneModels", "TDNA-Seq"];
                break;
        }
    } else {
        tracks = ["Araport11_Loci", "Araport11_GeneModels", "TDNA-Seq"];
    }
    return tracks;
}

module.exports = {
  getDefaultJBrowse2Url,
  getSlimViewUrl2,
  getFullViewUrl2,
  getMapElementViewUrl2,
  getTair12MapElementViewUrl
};